import { useEffect, useState } from "react";
import {
  LocalAudioTrack,
  LocalParticipant,
  LocalTrack,
  LocalVideoTrack,
  RemoteAudioTrack,
  RemoteDataTrack,
  RemoteParticipant,
  RemoteTrack,
  RemoteVideoTrack,
  VideoTrack,
} from "twilio-video";
import {
  getExistingAudioTrack,
  getExistingVideoTrack,
} from "../components/helpers/getExistingTrack";
import { getTrack } from "../utils/getTrack";
import { useChat } from "./useChat";
import { useRemoteParticipant } from "./useRemoteParticipant";
import { useLocalParticipant } from "./useLocalParticipant";
import { useRoom } from "./useRoom";
import { useVideoChat } from "./useVideoChat";

const useParticipantTracks = (
  participant: RemoteParticipant | LocalParticipant,
  isLocalParticipant: boolean
): {
  isFrontFacing: boolean;
  cameraTrack: LocalVideoTrack | RemoteVideoTrack | undefined;
  screenTrack: VideoTrack | undefined;
  audioTrack: LocalAudioTrack | RemoteAudioTrack | undefined;
} => {
  const { handleChangeFocused, currentFocused, room } = useRoom();
  const { permission } = useVideoChat();
  const { loadMessages } = useChat();
  const { handleOnBackground, setNetworkQualityLevelRemote } = useRemoteParticipant();
  const { setNetworkQualityLevelLocal, audioOff } = useLocalParticipant();

  const [cameraTrack, setCameraTrack] = useState<LocalVideoTrack | RemoteVideoTrack>();
  const [screenTrack, setScreenTrack] = useState<VideoTrack>();
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | RemoteAudioTrack>();
  const [isFrontFacing, setIsFrontFacing] = useState<boolean>(true);

  // Define os tracks do participante e adiciona listeners para adição/remoção de tracks do mesmo
  useEffect(() => {
    const trackSubscribed = (
      track: LocalTrack | RemoteTrack
    ) => {
      console.log("Track subscribed:", track);
      // Adiciona track ao estado de acordo com o tipo da track adicionada
      switch (track.kind) {
        case "video":
          if (track.name.startsWith("screen")) {
            setScreenTrack(track as VideoTrack);
          } else {
            setCameraTrack(track as LocalVideoTrack | RemoteVideoTrack);
            if (!isLocalParticipant && currentFocused === "local") {
              handleChangeFocused("remote", track as LocalVideoTrack | RemoteVideoTrack);
            }
          }
          break;
        case "audio":
          setAudioTrack(track as LocalAudioTrack | RemoteAudioTrack);
          break;
        case "data":
          track.on("message", (data: string) => {
            switch (data) {
              case "foreground":
                if (permission === "professional") handleOnBackground(false);
                break;
              case "background":
                if (permission === "professional") handleOnBackground(true);
                break;
              default:
                loadMessages(true);
                break;
            }
          });
          break;
        default:
          break;
      }
      
      // Caso seja o participante local, publica a track assim que ela for adicionada
      // e habilita o audio caso a track seja de audio 
      if (isLocalParticipant && room) {
        room.localParticipant.publishTrack(track as LocalTrack);
        console.log("Published track:", track);
        if (track.kind === "audio") {
          (track as LocalAudioTrack).enable();
          if (audioOff) {
            (track as LocalAudioTrack).disable();
          }
        }
      }
    };

    const trackUnsubscribed = (track: RemoteAudioTrack | RemoteVideoTrack) => {
      console.log("Track unsubscribed:", {...track});
      // Remove track do estado de acordo com o tipo da track removida
      // Se a track removida for a de camera e o participante for remoto,
      // troca o foco para a camera do participante local
      if (track.kind === "video") {
        if (track.name.startsWith("screen")) {
          setScreenTrack(undefined);
        } else {
          setCameraTrack(undefined);
          if (!isLocalParticipant) {
            const localParticipant = room?.localParticipant;
            if (localParticipant) {
              handleChangeFocused("local", getTrack("camera", getExistingVideoTrack(localParticipant)) as RemoteVideoTrack);
            }
          }
        }
      } else if (track.kind === "audio") {
        setAudioTrack(undefined);
      }
    };

    const handleTrackFrontFacing = (track: LocalTrack | null) => {
      if (track && track.kind === "video") {
        const mediaStreamTrackSettings = track.mediaStreamTrack.getSettings();
        setIsFrontFacing(mediaStreamTrackSettings.facingMode === "user");
      }
    };

    if (!cameraTrack) {
      setCameraTrack(
        getTrack("camera", getExistingVideoTrack(participant)) as RemoteVideoTrack
      );
    }
    if (!screenTrack) {
      setScreenTrack(
        getTrack("screen", getExistingVideoTrack(participant)) as VideoTrack
      );
    }
    if (!audioTrack) {
      setAudioTrack(
        getTrack("microphone", getExistingAudioTrack(participant)) as RemoteAudioTrack
      );
    }

    participant.on("trackSubscribed", trackSubscribed);
    participant.on("trackUnsubscribed", trackUnsubscribed);
    participant.on("trackStarted", handleTrackFrontFacing);

    const isTrackType = (track: any): track is RemoteAudioTrack | VideoTrack | RemoteDataTrack => {
      return track.kind === "audio" || track.kind === "video" || track.kind === "data";
    };

    // Adiciona tracks já existentes no participante
    participant.tracks.forEach(publication => {
      if (publication.track && (publication.track.kind === "video" || publication.track.kind === "audio" || publication.track.kind === "data")) {
        if (isTrackType(publication.track)) {
          trackSubscribed(publication.track);
        }
      }
    });

    const handleNetworkQuality = (level: number) => {
      if (isLocalParticipant) {
        setNetworkQualityLevelLocal(level);
      } else {
        setNetworkQualityLevelRemote(level);
      }
    }

    participant.on("networkQualityLevelChanged", handleNetworkQuality);

    if (isLocalParticipant) {
      participant.videoTracks.forEach((videoTrack) =>
        handleTrackFrontFacing(videoTrack.track as LocalTrack)
      );
    }

    return () => {
      setCameraTrack(undefined);
      setScreenTrack(undefined);
      setAudioTrack(undefined);
      participant.off("trackSubscribed", trackSubscribed);
      participant.off("trackUnsubscribed", trackUnsubscribed);
      participant.off("trackStarted", handleTrackFrontFacing);
      participant.off("networkQualityLevelChanged", handleNetworkQuality);
      // Não chamar removeAllListeners() nas publicações: remove listeners internos do twilio-video (AMP-58844)
    };
  }, [participant, handleChangeFocused, room]);

  return { isFrontFacing, cameraTrack, screenTrack, audioTrack };
};

export default useParticipantTracks;
